import type { ChangeEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useTranslate } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setComment } from 'store/payment/slice';
import { TextArea } from 'views/components/base/ui/text-area';

const ALLOWED_CHARS_PATTERN = 'a-zA-Zа-яА-ЯёЁӘәҒғҚқҢңӨөҰұҮүҺһІі0-9\\s.,!?';

const INVALID_CHARS_REGEX = new RegExp(`[^${ALLOWED_CHARS_PATTERN}]`);
const SANITIZE_REGEX = new RegExp(`[^${ALLOWED_CHARS_PATTERN}]`, 'g');

const DescriptionField = ({ isMobile }: { isMobile?: boolean }) => {
  const { t } = useTranslate();
  const { comment, paymentData } = useAppSelector((state) => state.payment);
  const { control, setValue } = useForm({
    mode: 'onChange',
  });
  const dispatch = useAppDispatch();

  const handleChangeComment = (
    e: ChangeEvent<HTMLTextAreaElement>,
    fieldOnChange?: (value: string) => void,
  ) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(SANITIZE_REGEX, '');

    if (fieldOnChange) {
      fieldOnChange(sanitizedValue);
    }

    if (paymentData) {
      setValue('description', sanitizedValue, { shouldValidate: paymentData.requiredDescription });
      dispatch(setComment(sanitizedValue));
    }
  };

  return paymentData ? (
    <form className="w-full md:max-w-none">
      <Controller
        name="description"
        control={control}
        rules={{
          required: paymentData?.requiredDescription ? t('field_required') : false,
          validate: (value) => {
            if (paymentData?.requiredDescription && (!value || value.length < 1)) {
              return t('field_required');
            }

            if (value && INVALID_CHARS_REGEX.test(value)) {
              return t('invalid_characters');
            }

            return true;
          },
        }}
        defaultValue=""
        render={({ field, fieldState: { error } }) => (
          <TextArea
            {...field}
            name="description"
            value={comment}
            isMobile={isMobile}
            onChange={(e) => handleChangeComment(e, field.onChange)}
            label={paymentData?.placeholder ? paymentData?.placeholder : t('comment')}
            upperLabel
            required={paymentData?.requiredDescription}
            inputClassName="text-left md:max-w-none overflow-hidden"
            placeholder={t('1xbet.input.comment.placeholder')}
            error={error?.message}
          />
        )}
      />
    </form>
  ) : null;
};

export { DescriptionField };
